import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Grid, Tooltip, IconButton, Paper, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable/';
import EditKitchenDialog from 'components/popups/EditKitchenDialog';
import ConfirmationDialog from 'components/popups/ConfirmationDialog';
import KitchenService from 'services/KitchenService';
import SalesPersonService from 'services/SalesPersonService';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import { USER_PERMISSIONS } from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';

function Kitchens() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [kitchenList, setKitchenList] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isEnableSearchRefreshButton, setIsEnableSearchRefreshButton] = useState(false);
  const [salespersons, setSalespersons] = useState([]);

  const userHasStockWritePermission = user?.shop_permissions?.stock_permission === USER_PERMISSIONS.WRITE;

  const openEditKitchenDialog = (kitchen) => {
    setEditData(kitchen);
  };

  const closeEditKitchenDialog = () => {
    setEditData(null);
  };

  const openAddKitchenDialog = () => {
    setShowAddDialog(true);
  };

  const closeAddKitchenDialog = () => {
    setShowAddDialog(false);
  };

  const [deleteKitchenData, setDeleteKitchenData] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteKitchen = async () => {
    if (!deleteKitchenData) return;
    setDeleting(true);
    try {
      await KitchenService.deleteKitchen(deleteKitchenData.id);
      postAlertMessage({
        text: tCommon('Kitchen deleted successfully') || 'Kitchen deleted successfully',
        type: 'success',
      });
      setDeleteKitchenData(null);
      getKitchens();
    } catch (error) {
      postAlertMessage({
        text: error.message || 'Failed to delete kitchen',
        type: 'failed',
      });
    }
    setDeleting(false);
  };

  const getKitchens = useCallback(
    async ({ search } = {}) => {
      setLoadingIndicator(true);
      try {
        const response = await KitchenService.getKitchens({
          search: search || undefined,
        });
        const data = response?.results || response || [];
        setKitchenList(data);
        setIsEnableSearchRefreshButton(false);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [postAlertMessage]
  );

  useEffect(() => {
    if (!shop?.id) return;
    getKitchens();
  }, [shop?.id, getKitchens]);

  useEffect(() => {
    const fetchSalespersons = async () => {
      try {
        const res = await SalesPersonService.getSalesPersons();
        setSalespersons(res || []);
      } catch (err) {
        console.error('Failed to fetch salespersons', err);
      }
    };
    fetchSalespersons();
  }, []);

  const headerData = useMemo(() => {
    const isWarehouseAvailable = shop && !featureIsExpired(shop?.warehouse_validity);
    const isKotAvailable = shop && (!featureIsExpired(shop?.kot_validity) || !featureIsExpired(shop?.kot_lite_validity));

    const columns = [
      {
        label: tCommon('ID') || 'ID',
        id: 'id',
        type: 'text',
      },
      {
        label: tCommon('Kitchen Name') || 'Kitchen Name',
        id: 'name',
        type: 'text',
      },
    ];

    if (isWarehouseAvailable) {
      columns.push({
        label: tCommon('Warehouse') || 'Warehouse',
        id: 'warehouse_name',
        type: 'callback',
        viewRender: (item) => item.warehouse?.name || '-',
      });
    }

    columns.push({
      label: tCommon('Chef') || 'Chef',
      id: 'chef',
      type: 'callback',
      viewRender: (item) => item.chef?.name || '-',
    });

    if (isKotAvailable) {
      columns.push({
        label: tCommon('Printer IP') || 'Printer IP',
        id: 'printer_ip',
        type: 'text',
      });
    }

    columns.push(
      {
        label: tCommon('Categories') || 'Categories',
        id: 'categories',
        type: 'callback',
        viewRender: (item) => {
          return item.categories?.map((c) => c.name).join(', ') || '-';
        },
      },
      {
        label: tCommon('Actions') || 'Actions',
        id: 'actions',
        type: 'callback',
        viewRender: (item) => {
          return (
            <Grid className={styles.btnGroup}>
              {userHasStockWritePermission && (
                <>
                  <Tooltip title={tCommon('Edit Kitchen') || 'Edit Kitchen'} onClick={() => openEditKitchenDialog(item)}>
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tCommon('Delete Kitchen') || 'Delete Kitchen'} onClick={() => setDeleteKitchenData(item)}>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Grid>
          );
        },
      }
    );

    return columns;
  }, [shop, userHasStockWritePermission, tCommon]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Kitchen') || 'Kitchen'}
          <span className={styles.menuTitle}>{tCommon('Management') || 'Management'}</span>
        </h2>
      </div>
      <>
        <div className={styles.changeable}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={3}>
              <h2 className={styles.subTitle}>{tCommon('Kitchens') || 'Kitchens'}</h2>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Grid container spacing={2} justifyContent="flex-end" alignItems="center">
                {userHasStockWritePermission && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ backgroundColor: '#ff851b', textTransform: 'none' }}
                      onClick={openAddKitchenDialog}
                    >
                      <RestaurantIcon fontSize="small" style={{ marginRight: '6px' }} />
                      {tCommon('New Kitchen') || 'New Kitchen'}
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} sm={4}>
                  <div className={styles.searchSec}>
                    <input
                      type="text"
                      value={filterText}
                      onChange={(e) => {
                        setFilterText(e.target.value);
                        setIsEnableSearchRefreshButton(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          getKitchens({ search: e.target.value.trim() });
                        }
                      }}
                      className={styles.searchInput}
                      placeholder={tCommon('Search Kitchens') || 'Search Kitchens'}
                    />
                    <Tooltip title="Refresh">
                      <IconButton size="small" onClick={() => getKitchens()} disabled={!isEnableSearchRefreshButton}>
                        <SyncIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>

        {(kitchenList && kitchenList.length > 0) || loadingIndicator ? (
          <Paper className={styles.tableWrapper}>
            <DataTable columns={headerData} rows={kitchenList} isLoading={loadingIndicator} />
          </Paper>
        ) : (
          !loadingIndicator && (
            <Info title={tOperations('No Kitchens Found') || 'No Kitchens Found'} content={tOperations('You have no kitchens to list.') || 'You have no kitchens to list.'} />
          )
        )}

        {showAddDialog && (
          <EditKitchenDialog
            closeDialog={closeAddKitchenDialog}
            editData={null}
            reloadKitchens={() => getKitchens()}
            salespersons={salespersons}
          />
        )}

        {editData && (
          <EditKitchenDialog
            closeDialog={closeEditKitchenDialog}
            editData={editData}
            reloadKitchens={() => getKitchens()}
            salespersons={salespersons}
          />
        )}

        {deleteKitchenData && (
          <ConfirmationDialog
            open={Boolean(deleteKitchenData)}
            handleClose={() => setDeleteKitchenData(null)}
            handleConfirm={handleDeleteKitchen}
            title={tCommon('Delete Kitchen') || 'Delete Kitchen'}
            message={tCommon('Are you sure you want to delete this kitchen? This will also remove the kitchen from all categories using it.') || 'Are you sure you want to delete this kitchen? This will also remove the kitchen from all categories using it.'}
            confirmText={tCommon('Delete') || 'Delete'}
            cancelText={tCommon('Cancel') || 'Cancel'}
            loading={deleting}
            type="warning"
          />
        )}
      </>
    </div>
  );
}

export default withConsoleBase(Kitchens);
